import { fetchSavings } from '../Product/components/ProductDetails/ProductDetails.utils';
/**
 * Helper function to extract PIM data from a cart line item
 * @param lineItem - Cart line item object
 * @returns Parsed PIM data or null if not found
 */
export var extractPimDataFromLineItem = function (lineItem) {
    var _a;
    try {
        var pimDataAttribute = (_a = lineItem.node.attributes) === null || _a === void 0 ? void 0 : _a.find(function (attribute) { return attribute.key === '_pimData'; });
        if (!(pimDataAttribute === null || pimDataAttribute === void 0 ? void 0 : pimDataAttribute.value)) {
            return null;
        }
        return JSON.parse(pimDataAttribute.value);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error extracting PIM data from line item:', error);
        return null;
    }
};
/**
 * Generates header data for QBS Group component using group items and QBS offer data
 * @param groupItems - Array of cart line item objects
 * @param qbsOfferData - String containing applicable QBS offer information separated by colons
 * @returns Header data object containing title, totalQuantity and optionally a subtitle
 */
export var createQbsGroupHeader = function (groupItems, qbsOfferData) {
    // Get QBS offer information
    var _a = qbsOfferData.split(':'), quantity = _a[0], displayName = _a[1], discountAmount = _a[2];
    // Create object to store header data
    var headerData = {
        title: '',
        totalQuantity: 0
    };
    // Generate the header title - properly handle grammar and singularizing the display name
    var startsWithVowel = ['a', 'e', 'i', 'o', 'u'].includes(displayName[0].toLowerCase());
    var titleCopyDisplayName = displayName[displayName.length - 1] === 's'
        ? displayName.slice(0, displayName.length - 1)
        : displayName;
    headerData.title = "Build ".concat(startsWithVowel ? 'an' : 'a', " ").concat(titleCopyDisplayName, " Pack");
    // Generate total quantity of group items
    groupItems.forEach(function (item) {
        headerData.totalQuantity += item.node.quantity;
    });
    // Generate the header subtitle
    // If total quantity is less than required for offer - create dynamic offer messaging
    // If at least one item has a discount applied & quantity has been reached - create fallback offer messaging
    // If none of these cases are met, there is a data problem and we should not add a subtitle
    if (headerData.totalQuantity < Number(quantity)) {
        var quantityNeeded = Number(quantity) - headerData.totalQuantity;
        var subtitleCopyDisplayName = quantityNeeded === 1 && displayName === 'Socks'
            ? displayName.slice(0, displayName.length - 1)
            : displayName;
        headerData.subtitle = "Add ".concat(quantityNeeded, " ").concat(subtitleCopyDisplayName, " to Save ").concat(discountAmount, "%");
    }
    else if (groupItems.some(function (item) { return item.node.discountAllocations.length; })) {
        headerData.subtitle = "Add More ".concat(displayName, " and Save ").concat(discountAmount, "%");
    }
    return headerData;
};
/**
 * Helper function to check if a line item has applicable QBS offers
 * @param lineItem - Cart line item object
 * @param qbsDiscountData - QBS discount data array
 * @returns Array of QBS savings or null if none found
 */
export var getLineItemQbsSavings = function (lineItem, qbsDiscountData) {
    var pimData = extractPimDataFromLineItem(lineItem);
    if (!pimData) {
        return null;
    }
    var _a = pimData.category, category = _a === void 0 ? [] : _a, _b = pimData.endUse, endUse = _b === void 0 ? '' : _b, _c = pimData.material, material = _c === void 0 ? '' : _c, _d = pimData.packSize, packSize = _d === void 0 ? 0 : _d;
    return fetchSavings(category, { endUse: endUse, primaryMaterial: material }, lineItem.node.merchandise.sku || '', packSize, qbsDiscountData);
};
/**
 * Reorganizes cart line items to group QBS items together while maintaining proper order
 * @param cartLineItems - Array of cart line items
 * @param qbsDiscountData - QBS discount data array
 * @returns Reorganized array containing QBS group objects and individual line items
 */
export var reorganizeCartWithQbsGroups = function (cartLineItems, qbsDiscountData) {
    if (cartLineItems === void 0) { cartLineItems = []; }
    if (qbsDiscountData === void 0) { qbsDiscountData = []; }
    if (!cartLineItems.length || !qbsDiscountData.length) {
        return cartLineItems;
    }
    var itemsMap = new Map();
    cartLineItems.forEach(function (lineItem, originalIndex) {
        try {
            var savings = getLineItemQbsSavings(lineItem, qbsDiscountData);
            if (!savings || !savings.length) {
                itemsMap.set(originalIndex, lineItem);
                return;
            }
            var firstTierSavings = savings[0];
            var qbsOfferKey = "".concat(firstTierSavings.quantity, ":").concat(firstTierSavings.displayName, ":").concat(firstTierSavings.discount);
            var existingGroup = itemsMap.get(qbsOfferKey);
            if (existingGroup) {
                existingGroup.push(lineItem);
            }
            else {
                itemsMap.set(qbsOfferKey, [lineItem]);
            }
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error processing cart item for QBS grouping:', error);
            itemsMap.set(originalIndex, lineItem);
        }
    });
    var resultArray = [];
    itemsMap.forEach(function (value, key) {
        if (typeof key === 'string') {
            var qbsOfferKey = key;
            var items = value;
            try {
                var headerData = createQbsGroupHeader(items, qbsOfferKey);
                var qbsGroupObject = {
                    type: 'qbs-group',
                    id: qbsOfferKey,
                    header: headerData,
                    groupItems: items
                };
                resultArray.push(qbsGroupObject);
            }
            catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error creating QBS group header for offer key \"".concat(qbsOfferKey, "\":"), error);
                items.forEach(function (fallbackItem) { return resultArray.push(fallbackItem); });
            }
        }
        else {
            resultArray.push(value);
        }
    });
    return resultArray;
};
export default {
    reorganizeCartWithQbsGroups: reorganizeCartWithQbsGroups,
    extractPimDataFromLineItem: extractPimDataFromLineItem,
    getLineItemQbsSavings: getLineItemQbsSavings,
    createQbsGroupHeader: createQbsGroupHeader
};
